import React from "react"
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import SharedQueryDevTools from "@myvp/shared/src/dev-tools/shared-query-dev-tools"
import SharedTanstackDevTools from "@myvp/shared/src/dev-tools/shared-tanstack-dev-tools"
import getUserLocale from "@myvp/shared/src/i18n/get-user-locale"
import { SUPPORTED_LOCALES, resolveSupportedLocale } from "src/i18n/locales"
import {
  defaultReactQueryCacheOptions,
  defaultReactQueryClientOptions,
} from "@myvp/shared/src/default-react-query-client-options"
import enUS from "src/i18n/languages/enUS"
import eproViewerEnUs from "@myvp/epro-viewer/src/i18n/languages/enUS"
import studyPreviewEnUs from "@myvp/study-preview/src/i18n/languages/enUS"
import { loadStudyPreviewLanguage } from "@myvp/study-preview/src/i18n/load-study-preview-language"
import { RootUIProviders } from "@myvp/shared/src/ui-providers"

export const queryClient = new QueryClient({
  defaultOptions: defaultReactQueryClientOptions,
  queryCache: new QueryCache(defaultReactQueryCacheOptions),
})

/**
 * For jest test usage.
 */
export const clearQueryClient = () => {
  queryClient.clear()
}

const Providers = (props: {
  children?: React.ReactNode
  /**
   * This should only be provided in test cases.
   */
  staticTestMessages?: Record<string, string>
}) => {
  let ui: React.ReactNode = props.children

  ui = (
    <RootUIProviders
      resolveLocale={getLocale}
      loadMessages={loadMessages}
      staticTestMessages={props.staticTestMessages}
    >
      {ui}
    </RootUIProviders>
  )

  // any providers that need to use a queryClient (or useQuery) should go before this
  ui = (
    <QueryClientProvider client={queryClient}>
      {ui}
      <SharedQueryDevTools />
      <SharedTanstackDevTools />
    </QueryClientProvider>
  )

  return ui
}

export default Providers

const loadSponsorMessages = async (messagesKey: string) =>
  (await import(`src/i18n/languages/${messagesKey}`)).default

const loadMessages = async (locale: string) => {
  const messagesKey = locale.replaceAll("-", "")
  const messages = await Promise.all([
    studyPreviewEnUs,
    loadStudyPreviewLanguage(messagesKey).catch((e) => {
      // oxlint-disable-next-line no-console
      console.error(
        `Failed to load @myvp/study-preview/src/i18n/languages/${messagesKey}`,
        e
      )
    }),
    eproViewerEnUs,
    enUS,
    loadSponsorMessages(messagesKey).catch((e) => {
      // oxlint-disable-next-line no-console
      console.error(`Failed to load src/i18n/languages/${messagesKey}`, e)
    }),
  ])
  return Object.assign({}, ...messages)
}

// NOTE: instead of exporting this, use useI18nState instead
const getLocale = (localeOverride?: string) => {
  const userLocale = getUserLocale(localeOverride)
  return resolveSupportedLocale(userLocale) ?? SUPPORTED_LOCALES.enUS
}
